// Inject dropdown styles into <head> once (dropdown lives in document.body)
if (!document.getElementById('multi-select-dropdown-styles')) {
    const globalStyle = document.createElement('style');
    globalStyle.id = 'multi-select-dropdown-styles';
    globalStyle.textContent = `
      .ms-dropdown {
        display: none;
        position: fixed;
        background-color: white;
        border: 1px solid black;
        padding: 4px;
        min-width: 150px;
        box-sizing: border-box;
        z-index: 9999;
      }
      .ms-dropdown.open {
        display: block;
      }
      .ms-dropdown * {
        box-sizing: border-box;
      }
      .ms-dropdown .ms-search {
        margin-bottom: 4px;
        width: 100%;
        font-family: inherit;
      }
      .ms-dropdown .ms-items {
        display: flex;
        flex-direction: column;
        overflow: auto;
      }
      .ms-dropdown .ms-no-items {
        align-items: center;
        height: 28px;
        opacity: 0.8;
        font-style: italic;
        font-size: inherit;
        color: inherit;
        font-family: inherit;
        padding: 0 6px;
        display: none;
      }
      .ms-dropdown .ms-no-items.visible {
        display: flex;
      }
      .ms-dropdown .ms-item {
        display: flex;
        align-items: center;
        height: 28px;
        user-select: none;
      }
      .ms-dropdown .ms-item.hidden {
        display: none;
      }
      .ms-dropdown .ms-item:hover {
        background-color: #e1e1e1;
      }
      .ms-dropdown .ms-item label {
        width: 100%;
        height: 100%;
        cursor: pointer;
        display: flex;
        align-items: center;
      }
    `;
    document.head.appendChild(globalStyle);
}

class MultiSelect extends HTMLElement {
    static observedAttributes = ["empty-placeholder", "disabled"];
    static formAssociated = true;

    private _internals: ElementInternals;
    private _slot: HTMLSlotElement;
    private _itemsContainer: HTMLDivElement;
    private _select: HTMLButtonElement;
    private _selectText: HTMLSpanElement;
    private _dropdown: HTMLDivElement;
    private _searchField: HTMLInputElement;
    private _noItemsElement: HTMLDivElement;
    private _windowScrollFunction = this.onWindowScroll.bind(this);
    private _windowClickFunction = this.onWindowClick.bind(this);
    private _ignoreNextWindowClick = false;
    private _searchCache = new Map<HTMLDivElement, string>();
    private _items: HTMLDivElement[] = [];
    private _onSearchInput = this.debounce(this.performSearch.bind(this), 250);
    private _emptyPlaceholder: string = '';
    private _selectedItems: HTMLDivElement[] = [];
    private _instanceId = String(Math.random()).slice(2, 10);

    constructor() {
        super();
    }

    connectedCallback() {
        const shadow = this.attachShadow({ mode: "open" });

        this._select = document.createElement('button');
        this._select.setAttribute('part', 'select');
        this._select.type = 'button';
        this._select.addEventListener('click', () => this.onSelectClicked());

        this._selectText = document.createElement('span');
        this._selectText.textContent = this._emptyPlaceholder;

        const chevronGetter = document.createElement('div');
        chevronGetter.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><!--!Font Awesome Free v7.1.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2026 Fonticons, Inc.--><path d="M297.4 470.6C309.9 483.1 330.2 483.1 342.7 470.6L534.7 278.6C547.2 266.1 547.2 245.8 534.7 233.3C522.2 220.8 501.9 220.8 489.4 233.3L320 402.7L150.6 233.4C138.1 220.9 117.8 220.9 105.3 233.4C92.8 245.9 92.8 266.2 105.3 278.7L297.3 470.7z"/></svg>';
        const chevron = chevronGetter.firstElementChild;

        this._dropdown = document.createElement('div');
        this._dropdown.classList.add('ms-dropdown');
        this._dropdown.addEventListener('click', () => this._ignoreNextWindowClick = true);

        const searchContainer = document.createElement('div');

        this._searchField = document.createElement('input');
        this._searchField.classList.add('ms-search');
        this._searchField.placeholder = 'S\u00F8g';
        this._searchField.addEventListener('keyup', () => this._onSearchInput(this._searchField.value));

        this._itemsContainer = document.createElement('div');
        this._itemsContainer.classList.add('ms-items');

        this._slot = document.createElement('slot');
        this._slot.addEventListener('slotchange', () => this.onSlotChange());

        const style = document.createElement("style");
        style.textContent = `
          :host {
            position: relative;
          }

          ::slotted(*) {
            display: none;
          }

          * {
            box-sizing: border-box;
          }

          button[part=select] {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          button[part=select] svg {
            width: 14px;
            height: 14px;
            margin-left: 5px;
            fill: currentColor;
          }

          button[part=select] span {
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
          }

          :host([disabled]) {
            opacity: 0.5;
            pointer-events: none;
          }
        `;



        shadow.appendChild(style);
        shadow.appendChild(this._select);
        shadow.appendChild(this._slot);
        this._dropdown.appendChild(searchContainer);
        this._dropdown.appendChild(this._itemsContainer);
        searchContainer.appendChild(this._searchField);
        this._select.appendChild(this._selectText);
        this._select.appendChild(chevron);

        this._internals = this.attachInternals();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'empty-placeholder') {
            this._emptyPlaceholder = newValue;
            if (this._selectText && this._selectedItems.length === 0) {
                this._selectText.textContent = this._emptyPlaceholder;
            }
        } else if (name === 'disabled') {
            const isDisabled = newValue !== null;
            if (this._select) {
                this._select.disabled = isDisabled;
            }
            if (isDisabled) {
                this.closeDropdown();
            }
        }
    }

    private onSlotChange() {
        this._itemsContainer.innerHTML = '';
        this._items = [];
        this._searchCache.clear();
        this._selectedItems = [];

        this._noItemsElement = document.createElement('div');
        this._noItemsElement.textContent = 'Ingen matches';
        this._noItemsElement.classList.add('ms-no-items');
        this._itemsContainer.appendChild(this._noItemsElement);

        const items = this._slot.assignedElements({ flatten: true });

        items.forEach((item: HTMLElement, index) => {
            const wrapper = document.createElement('div');
            wrapper.classList.add('ms-item');

            const label = document.createElement('label');
            label.setAttribute('for', 'cb-' + this._instanceId + '-' + index.toString(10));

            const checkbox = document.createElement("input");
            checkbox.type = "checkbox";
            checkbox.value = item.dataset.value;
            checkbox.id = 'cb-' + this._instanceId + '-' + index.toString(10);
            checkbox.checked = item.dataset.selected !== undefined;
            checkbox.addEventListener('change', () => this.onItemSelectionChanged(wrapper, checkbox));

            const content = item.cloneNode(true);

            wrapper.append(checkbox, label);
            label.appendChild(content);

            this._itemsContainer.appendChild(wrapper);
            this._items.push(wrapper);
            this._searchCache.set(wrapper, (content.textContent ?? '').toLowerCase());

            if (checkbox.checked) {
                this._selectedItems.push(wrapper);
            }
        });

        this._updateSelectText();
        this._syncFormValue();
        this._updateValidity();
    }

    private _updateSelectText() {
        if (this._selectedItems.length === 0) {
            this._selectText.textContent = this._emptyPlaceholder;
        } else if (this._selectedItems.length === 1) {
            this._selectText.textContent = this._selectedItems[0].textContent;
        } else {
            this._selectText.textContent = `${this._selectedItems.length} valgte`;
        }
    }

    private onItemSelectionChanged(item: HTMLDivElement, checkbox: HTMLInputElement) {
        if (checkbox.checked) {
            this._selectedItems.push(item);
        } else {
            this._selectedItems.splice(this._selectedItems.indexOf(item), 1);
        }

        this._updateSelectText();

        this._syncFormValue();
        this._updateValidity();

        this.dispatchEvent(new Event("change", { bubbles: true }));
    }

    private onSelectClicked() {
        this._ignoreNextWindowClick = true;
        if (this._dropdown.classList.contains('open')) {
            this.closeDropdown();
        } else {
            const rect = this._select.getBoundingClientRect();
            this._dropdown.style.top = `${rect.bottom + 2}px`;
            this._dropdown.style.left = `${rect.left}px`;
            this._dropdown.style.width = `${rect.width}px`;
            document.body.appendChild(this._dropdown);
            this._dropdown.classList.add('open');
            window.addEventListener('scroll', this._windowScrollFunction, true);
            window.addEventListener('click', this._windowClickFunction);
            this.dispatchEvent(new Event('opened'));
        }
    }

    private onWindowScroll(e: Event) {
        if (this._dropdown.contains(e.target as Node)) return;
        this.closeDropdown();
    }

    private onWindowClick() {
        if (this._ignoreNextWindowClick) {
            this._ignoreNextWindowClick = false;
            return;
        }
        this.closeDropdown();
    }

    private closeDropdown() {
        this._dropdown.classList.remove('open');
        if (this._dropdown.parentNode === document.body) {
            document.body.removeChild(this._dropdown);
        }
        window.removeEventListener('click', this._windowClickFunction);
        window.removeEventListener('scroll', this._windowScrollFunction, true);
        this._ignoreNextWindowClick = false;
        this.dispatchEvent(new Event('closed'));
    }

    private performSearch(query: string) {
        query = query.toLowerCase();
        let hasVisibleItems = false;

        this._items.forEach(item => {
            const value = this._searchCache.get(item);
            if (value.indexOf(query) < 0) {
                item.classList.add('hidden');
            } else {
                item.classList.remove('hidden');
                hasVisibleItems = true;
            }
        });

        if (hasVisibleItems) {
            this._noItemsElement.classList.remove('visible');
        } else {
            this._noItemsElement.classList.add('visible');
        }
    }

    private debounce(fn: Function, delay = 1000): Function {
        let timerId = null;

        return (...args) => {
            clearTimeout(timerId);
            timerId = setTimeout(() => fn(...args), delay);
        };
    };

    /* ---------- form integration ---------- */

    _syncFormValue() {
        const values = this.value;

        if (values.length === 0) {
            this._internals.setFormValue(null);
            return;
        }

        const formData = new FormData();
        values.forEach(v => formData.append(this.name, v));

        this._internals.setFormValue(formData);
    }

    _updateValidity() {
        if (this.hasAttribute("required") && this.value.length === 0) {
            this._internals.setValidity(
                { valueMissing: true },
                "Vælg mindst én mulighed",
                this
            );
        } else {
            this._internals.setValidity({});
        }
    }

    /* ---------- standard input-like API ---------- */

    get name() {
        return this.getAttribute("name");
    }

    get value() {
        return this._selectedItems.map(cb => cb.querySelector<HTMLInputElement>('input[type=checkbox]').value);
    }

    set value(values) {
        const set = new Set(values);
        this._selectedItems = [];
        this._items.forEach(item => {
            const cb = item.querySelector<HTMLInputElement>('input[type=checkbox]');
            cb.checked = set.has(cb.value);
            if (cb.checked) {
                this._selectedItems.push(item);
            }
        });

        this._updateSelectText();

        this._syncFormValue();
        this._updateValidity();
    }

    formResetCallback() {
        this.value = [];
    }

    formStateRestoreCallback(state) {
        if (Array.isArray(state)) {
            this.value = state;
        }
    }
}

customElements.define('multi-select', MultiSelect);